<template>
  <InputTitle :name="name" :required="required" v-if="!isEmpty(name)" :notice-text="noticeText" />
  <div class="form-notice" v-if="useNotice">오전/오후 표기가 별도로 없습니다.</div>
  <div
    class="form-in"
    v-if="
      type !== 'datetime' &&
        type !== 'date' &&
        type !== 'birthDate' &&
        type !== 'select' &&
        type !== 'search' &&
        type !== 'time' &&
        type !== 'number'
    "
  >
    <label class="label" @click="fnClick">
      <input
        :type="type"
        :required="required"
        :placeholder="placeHolder"
        @input="updateValue"
        @keydown="onKeydown"
        @blur="emits('blur')"
        @compositionstart="isComposing = true"
        @compositionend="onCompositionEndMaxLength"
        v-model="inputValue"
        :readonly="readOnly"
        :lang="lang"
        :disabled="disabled"
        :inputmode="inputMode"
        :enterkeyhint="enterKeyHint"
      />
      <!-- 260508: HTML maxlength 제거 — 한글 IME composition 중 maxlength 강제가 마지막 마교체 입력을 차단하는 버그 (예: "광고플랫폼" 입력 불가).
           updateValue에서 JS slice로 대체 — composition 끝난 후에만 컷 감지. -->
    </label>
    <button class="btn border-primary regular" @click="buttonEvent" v-if="buttonText">{{ buttonText }}</button>
  </div>

  <!-- Date 타입: maxLength=16 -->
  <div class="form-in" v-if="type === 'date'">
    <label class="label" @click="fnClick">
      <input
        @focusout="focusOut"
        type="text"
        :required="required"
        :placeholder="placeHolder"
        @input="updateDateValue"
        inputmode="numeric"
        :maxlength="maxLength"
        v-model="inputValue"
        :readonly="readOnly"
        :lang="lang"
        :disabled="disabled"
      />
    </label>
  </div>

  <div class="form-in" v-if="type === 'number'">
    <label class="label" @click="fnClick">
      <input
        type="number"
        :required="required"
        :placeholder="placeHolder"
        @input="updateNumberValue"
        :value="inputValue"
        :readonly="readOnly"
        :disabled="disabled"
        inputmode="numeric"
        pattern="[0-9]*"
      />
    </label>
    <button class="btn border-primary regular" @click="buttonEvent" v-if="buttonText">{{ buttonText }}</button>
  </div>

  <!-- DateTime 타입: maxLength=16-->
  <div class="form-in" v-if="type === 'datetime'">
    <label class="label" @click="fnClick">
      <input
        type="text"
        :required="required"
        :placeholder="placeHolder"
        @input="updateDateTimeValue"
        inputmode="numeric"
        :maxlength="16"
        v-model="inputValue"
        :readonly="readOnly"
        :lang="lang"
        :disabled="disabled"
      />
    </label>
  </div>

  <!-- Time 타입: maxLength=16-->
  <div class="form-in" v-if="type === 'time'">
    <label class="label" @click="fnClick">
      <input
        type="text"
        :required="required"
        :placeholder="placeHolder"
        @input="updateTimeValue"
        inputmode="numeric"
        :maxlength="5"
        v-model="inputValue"
        :readonly="readOnly"
        :lang="lang"
        :disabled="disabled"
      />
    </label>
  </div>

  <div class="form-in" v-if="type === 'select'">
    <label class="label" @click="fnClick">
      <button class="select" @click="buttonEvent">
        {{ modelValue ? modelValue : placeHolder }}
      </button>
    </label>
  </div>

  <div class="form-in" v-if="type === 'search'">
    <label class="label" @click="fnClick">
      <input
        type="text"
        :required="required"
        :placeholder="placeHolder"
        @input="updateValue"
        @compositionstart="isComposing = true"
        @compositionend="onCompositionEndMaxLength"
        v-model="inputValue"
        :readonly="readOnly"
      />
      <!-- 261001 대표 제보(태그 5자인데 4자에서 막힘): 260508 과 같은 이유로 HTML maxlength 제거 — 이 '입력+버튼' 칸만 빠져 있었다.
           글자 수는 위 칸과 같이 updateValue·onCompositionEndMaxLength 가 조합이 끝난 뒤 자른다. -->
    </label>
    <button class="btn border-primary regular" @click="buttonEvent" v-if="buttonText">{{ buttonText }}</button>
  </div>
  <Validation :object-valid="valid" v-if="!isEmpty(valid)" />
  <Validation :custom-valid="customValid" v-if="!isEmpty(customValid)" />
</template>
<script setup>
import { isEmpty } from '@/common/utils/Util';
import { ref, watch } from 'vue';
import Validation from '@/components/common/layouts/Validation.vue';
import InputTitle from '@/components/common/input/InputTitle.vue';

const emits = defineEmits(['update:modelValue', 'input', 'buttonEvent', 'change', 'click', 'keyupEnter', 'blur']);
const props = defineProps({
  focusOut: {
    type: Function,
    default: null
  },
  useNotice: {
    type: Boolean,
    default: false
  },
  name: {
    type: String,
    default: ''
  },
  lang: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  required: {
    type: Boolean,
    default: true
  },
  placeHolder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: [Object, String, Number],
    default: null
  },
  valid: {
    type: [Object, String],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  customValid: {
    type: String,
    default: ''
  },
  inputEvent: {
    type: Function,
    default: null
  },
  buttonText: {
    type: String,
    default: ''
  },
  maxLength: {
    type: Number,
    default: null
  },
  inputMode: {
    type: String,
    default: 'text'
  },
  noticeText: {
    type: String,
    default: ''
  },
  isNumber: {
    type: Boolean,
    default: false
  },
  enterKeyHint: {
    type: String,
    default: ''
  }
});
const inputValue = ref(props.modelValue);
const isComposing = ref(false);
let pendingEnter = false;
let lastEnterTime = 0;

const onKeydown = (e) => {
  if (e.key === 'Enter' || e.keyCode === 13) {
    if (isComposing.value) {
      pendingEnter = true;
    } else {
      const now = Date.now();
      if (now - lastEnterTime > 300) {
        lastEnterTime = now;
        emits('keyupEnter');
      }
    }
  }
};

const onCompositionEnd = () => {
  isComposing.value = false;
  if (pendingEnter) {
    pendingEnter = false;
    const now = Date.now();
    if (now - lastEnterTime > 300) {
      lastEnterTime = now;
      emits('keyupEnter');
    }
  }
};

watch(
  () => props.modelValue,
  newValue => {
    inputValue.value = newValue;
  }
);

const fnClick = () => {
  emits('click');
};

const buttonEvent = () => {
  emits('buttonEvent');
};
const updateValue = event => {
  let rawValue = event.target.value;
  if (props.isNumber) {
    // 숫자만 허용
    const filtered = rawValue.replace(/[^0-9]/g, '');
    // maxLength 적용
    const sliced = props.maxLength ? filtered.slice(0, props.maxLength) : filtered;
    event.target.value = sliced;
    emits('update:modelValue', sliced);
    emits('input', sliced);
  } else {
    // 260508: 한글 IME composition 중에는 maxLength slice 안 함 — 완성 후만 감지
    //         ("광고플랫폼" 같은 받침 합체 단어 입력 보장)
    if (props.maxLength && !isComposing.value) {
      rawValue = rawValue.slice(0, props.maxLength);
    }
    event.target.value = rawValue;
    emits('update:modelValue', rawValue);
    emits('input');
  }
};

// 260508: composition 끝난 직후 maxLength 계산 (IME 결과 포함)
const onCompositionEndMaxLength = (event) => {
  onCompositionEnd();
  if (props.maxLength && !props.isNumber) {
    const value = event.target.value;
    if (value.length > props.maxLength) {
      const sliced = value.slice(0, props.maxLength);
      event.target.value = sliced;
      inputValue.value = sliced;
      emits('update:modelValue', sliced);
      emits('input');
    }
  }
};

const updateDateValue = event => {
  let number = event.target.value.replace(/[^0-9]/g, '').slice(0, 8);
  let value = '';
  if (number.length >= 7) {
    value = `${toYear(number)}-${toMonth(number)}-${toDay(number)}`;
  } else if (number.length >= 5) {
    value = `${toYear(number)}-${toMonth(number)}`;
  } else {
    value = number;
  }
  // 인풋 값 강제 수정 (한글,영문 제거된 값으로)
  event.target.value = value;
  emits('update:modelValue', value);
  emits('input');
};

const updateNumberValue = e => {
  let val = e.target.value;

  // 빈 문자열이면 그대로 유지
  if (val === '') {
    e.target.value = '';
    emits('update:modelValue', val);
    return;
  }
  // 문자열로 변환하여 처리
  val = String(val).replace(/[^0-9]/g, '');

  if (props.maxLength && val.length > props.maxLength) {
    val = val.slice(0, props.maxLength);
  }
  e.target.value = val;
  emits('update:modelValue', val);
  emits('input');
};
const updateDateTimeValue = event => {
  let value = event.target.value;
  let number = value.replace(/[^0-9]/g, ''); // value 의 "." ":" 제외하고 모두 제거

  // yyyy 년도 입력 완료 시 . 자동 입력
  if (number.length >= 5 && value[4] !== '.') {
    value = toYear(number) + '-' + toMonth(number); // value 4자리 + . 추가
  }

  if (number.length >= 7 && value[6] !== '.') {
    value = toYear(number) + '-' + toMonth(number) + '-' + number.slice(6); // value 6자리 + . 추가
  }

  // yyyy.mm.dd 입력 완료 시 " " 자동 입력 후 HH:mm 입력
  if (number.length >= 9 && value[8] !== ' ') {
    value = toYear(number) + '-' + toMonth(number) + '-' + toDay(number) + ' ' + number.slice(8); // value 8자리 + . 추가
  }

  if (number.length >= 11 && value[10] !== ':') {
    value = toYear(number) + '-' + toMonth(number) + '-' + toDay(number) + ' ' + toHour(number) + ':' + toMin(number); // value 10자리 + : 추가
  }

  emits('update:modelValue', value);
  emits('input');
};

const updateTimeValue = event => {
  let value = event.target.value;
  let number = value.replace(/[^0-9]/g, ''); // value 의 "." ":" 제외하고 모두 제거

  let hour = '00';

  if (number.length >= 2 && value[value.length - 1] !== ':') {
    hour = number.slice(0, 2);
    if (hour > 23) {
      hour = 23;
    }
    value = hour + ':';
  }

  if (number.length > 3) {
    let min = number.slice(2, 4);
    if (min > 59) {
      min = 59;
    }

    value = hour + ':' + min; // value 10자리 + : 추가
  }

  emits('update:modelValue', value);
  emits('input');
};

function toYear(num) {
  return num.slice(0, 4);
}
function toMonth(num) {
  let month = num.slice(4, 6) > 12 ? 12 : num.slice(4, 6);

  month = month === '00' ? '01' : month;
  return month;
}
function toDay(num) {
  let month = num.slice(4, 6) > 12 ? 12 : num.slice(4, 6);
  let day = num.slice(6, 8) > 31 ? '31' : num.slice(6, 8);
  day = day === '00' ? '01' : day;

  if (month === '02' && day > 28) {
    day = '28';
  }

  if (month === '04' || month === '06' || month === '09' || month === '11') {
    day = day > 30 ? '30' : day;
  }

  return day;
}
function toHour(num) {
  return num.slice(8, 10) > 23 ? '23' : num.slice(8, 10);
}
function toMin(num) {
  return num.slice(10) > 59 ? '59' : num.slice(10);
}
</script>
